import { AddIcon } from '@diligentcorp/atlas-react-icons';
import {
  DragAndDrop,
  DragAndDropItem,
  StyledDivider,
} from '@issuemanager/apps/client/components';
import {
  Box,
  Button,
  Stack,
  Typography,
  useEventCallback,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtendedActionDto } from '@issuemanager/api-client';
import { ReactNode, useState, JSX } from 'react';
import {
  ISSUE_WORKFLOW_EXECUTION_TYPE,
  IssueStatusEnum,
  IssueWorkflowExecutionType,
} from '@issuemanager/constants';
import { DragStart, DropResult } from '@hello-pangea/dnd';
import { styled } from '@mui/system';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { MinimalUserDto } from '@issuemanager/routes';
import ActionDetails from '../components/action-details';
import { EmptyWorkflowState } from '../components/empty-workflow-state';
import ActionGroup, { ActionFormValue } from '../components/action-group';
import { ActionFieldReturnType } from '../hooks/use-manage-issue-form';
import { useIssueOutletContext } from '../hooks/useIssueOutletContext';

export type ActionValues = {
  id?: ExtendedActionDto['id'];
  title?: ExtendedActionDto['title'];
  status: ExtendedActionDto['status'];
  approvalStatus?: ExtendedActionDto['approvalStatus'];
  description?: string | null;
  dueDate?: Date | number | null;
  owner?: MinimalUserDto | null;
  approver?: MinimalUserDto | null;
  attachments?: ExtendedActionDto['attachments'];
};

type Props<T extends ActionFormValue> = {
  issueFields: {
    executionType: ReactNode;
  };
  actionFields: ActionFieldReturnType[];
  actionsFormValues: T[];
  actionValues: ActionValues[];
  onAddAction: () => void;
  isEditing: boolean;
  onEdit?: () => void;
  onDragEnd?: (actions: T[]) => void;
  executionType: IssueWorkflowExecutionType;
  isEditable: boolean;
  isWorkflowBlocked?: boolean;
};

const StyledDragAndDropBox = styled(Box)(() => ({
  width: 'auto',
  maxWidth: '100%',
}));

export function ActionFields<T extends ActionFormValue>({
  issueFields,
  actionFields,
  actionsFormValues,
  actionValues,
  onAddAction,
  isEditing,
  onEdit,
  onDragEnd,
  executionType,
  isEditable,
  isWorkflowBlocked,
}: Props<T>): JSX.Element {
  const { t } = useTranslation(['issue', 'action']);
  const semantic = useAtlasSemanticThemeTokens();
  const { issue } = useIssueOutletContext() ?? {};

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const isSequential =
    executionType === ISSUE_WORKFLOW_EXECUTION_TYPE.SEQUENTIAL;
  const canDragAndDrop = isSequential;

  const items: DragAndDropItem<T>[] = actionFields.map((field, index) => {
    const canDragAndDropItem =
      canDragAndDrop && (field.isDraft || field.isPending);

    return {
      id: field.id,
      isDragDisabled: !canDragAndDropItem,
      data: actionsFormValues[index],
      content: (
        <ActionGroup
          field={field}
          index={index}
          key={`${field.id}`}
          executionType={executionType}
          actionFormValue={actionsFormValues[index]}
          isDragging={draggingIndex === index}
          isDragDisabled={!canDragAndDropItem}
          actionsCount={actionFields.length}
        />
      ),
    };
  });

  const handleDragStart = useEventCallback((event: DragStart) => {
    setDraggingIndex(event.source.index);
  });

  const handleDragEnd = useEventCallback(
    (orderedItems: DragAndDropItem<T>[], result: DropResult) => {
      if (!result.destination) return;

      setDraggingIndex(null);
      onDragEnd?.(orderedItems.map((item) => item.data!));
    },
  );

  const blockWorkflowStatus = [
    IssueStatusEnum.TO_BE_APPROVED,
    IssueStatusEnum.DISCARDED,
  ];

  return (
    <Stack data-testid='actions' spacing={3}>
      <Typography variant='h3' fontWeight='medium'>
        {t('issue:form.sections.workflow')}
      </Typography>

      {/* Need to have the form on the background because we need to apply validations on the
      form fields. */}
      <Box display={isEditing ? 'block' : 'none'} data-testid='actions-form'>
        <Box mb={4}>{issueFields.executionType}</Box>

        <Box borderRadius={2} overflow='hidden' bgcolor='#ffffff'>
          <StyledDragAndDropBox>
            <DragAndDrop
              items={items}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              isDisabled={!canDragAndDrop}
            />
          </StyledDragAndDropBox>

          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            paddingY={3}
            paddingX={2}
          >
            <StyledDivider
              flexItem
              sx={{
                borderColor: semantic.color.ui.divider.value,
              }}
            />
            <Button
              variant='text'
              startIcon={<AddIcon />}
              data-testid='add-action-button'
              size='small'
              onClick={onAddAction}
              sx={{ alignSelf: 'center' }}
              disabled={
                isWorkflowBlocked ||
                blockWorkflowStatus.includes(
                  issue?.data?.status as IssueStatusEnum,
                )
              }
            >
              {t('action:actions.addAction')}
            </Button>
            <StyledDivider flexItem />
          </Box>
        </Box>
      </Box>
      {!isEditing &&
        (!actionsFormValues.length && isEditable ? (
          <EmptyWorkflowState
            onEdit={onEdit}
            description={t('action:view.empty.description')}
          />
        ) : (
          <ActionDetails
            actions={actionValues}
            executionType={issueFields.executionType}
          />
        ))}
    </Stack>
  );
}
